import * as R from 'ramda'
import * as ReactRedux from 'react-redux'
import * as ReactRouter from 'react-router-dom'
import * as React from 'react'

import * as Analytics from '@rushplay/analytics'
import * as Common from '@rushplay/common'
import * as Forms from '@rushplay/forms'
import * as I18n from '@rushplay/i18n'
import css from '@styled-system/css'
import styled from '@emotion/styled'

import * as ServerConfiguration from './server-configuration'
import * as Constants from './constants'
import * as Cookies from './cookies-module'
import * as Player from './player'
import * as Utils from './utils'
import { HtmlContent } from './html-content'
import { InputField } from './input-field'
import { SubmitButton } from './submit-button'
import { useBriteDataSchema } from './use-brite-data-schema'
import { useSafeUpdateQuery } from './use-safe-update-query'

const SelectBox = styled.div`
  border-style: solid;
  border-radius: 8px;
  border-width: 2px;
  cursor: pointer;
  text-align: center;

  ${props =>
    css({
      bg: props.selected ? 'secondary' : null,
      borderColor: props.selected ? 'secondary' : null,
      fontSize: 4,
      opacity: props.selected ? '1' : '0.6',
      py: 0,
    })}
`

const SelectBoxChevronDown = styled.div`
  align-self: center;
  border-left-color: transparent;
  border-right-color: transparent;
  border-style: solid;
  border-width: 8px 8px 0px 8px;
  height: 0px;
  width: 0px;

  ${props =>
    css({
      borderBottomColor: props.selected ? 'secondary' : 'transparent',
      borderTopColor: props.selected ? 'secondary' : 'transparent',
      pb: 0,
    })}
`

function AmountOptions() {
  const { country } = ServerConfiguration.useContext()
  const amounts = Utils.Amounts.getInitialAmounts(country.alpha2)
  const field = Forms.useField('#/properties/amount', { noRegister: true })

  return (
    <Common.Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gridGap={0}>
      {R.map(
        amount => (
          <Common.Flex flexDirection="column" key={amount}>
            <SelectBox
              selected={amount === field.value}
              onClick={() => field.onChangeValue(amount)}
            >
              {amount / 100}
            </SelectBox>
            <SelectBoxChevronDown selected={amount === field.value} />
          </Common.Flex>
        ),
        amounts
      )}
    </Common.Box>
  )
}

export function BriteLoginForm() {
  const dispatch = ReactRedux.useDispatch()
  const i18n = I18n.useI18n()
  const currency = Player.useCurrency()
  const lastDepositAmount = ReactRedux.useSelector(state =>
    Player.getLastDepositAmount(state.player)
  )
  const [returningPlayer] = Cookies.useCookie(
    Constants.CookieKeys.RETURNING_PLAYER
  )
  const dataSchema = useBriteDataSchema(Constants.BriteLoginFlow.DEPOSIT)
  const initialValue = R.defaultTo(
    Utils.Amounts.getInitialAmount(),
    lastDepositAmount
  )
  const history = ReactRouter.useHistory()

  const briteLoginNormalQuery = useSafeUpdateQuery({
    'login-flow': 'normal',
    login: null,
  })
  const briteDepositQuery = useSafeUpdateQuery({
    'login-flow': 'deposit',
    login: null,
  })

  function handleSubmit(data) {
    // condition is added to avoid invoking selectAmount event twice for new players
    if (returningPlayer) {
      dispatch(Analytics.selectAmount(data.amount))
    }
    history.push(`?${briteDepositQuery}&amount=${data.amount}`)
  }

  return (
    <Common.Box maxWidth="500px" pb={2}>
      <Forms.Provider
        name="brite-login-form"
        schema={dataSchema}
        onSubmit={(errors, data) => {
          if (R.isEmpty(errors)) {
            handleSubmit(data)
          }
        }}
      >
        <Common.Box pt={0}>
          <AmountOptions />

          <InputField
            currency={currency}
            normalize={Utils.Amounts.normalizeAmount}
            initialValue={initialValue}
            inputMode="decimal"
            parse={Utils.Amounts.parseAmount}
            scope="#/properties/amount"
          />
          <Common.Box fontSize={[0, 1]} textAlign="center" pt={2}>
            <HtmlContent
              html={{
                __html: i18n.translate('brite-login-form.terms-and-privacy'),
              }}
            />
          </Common.Box>
          <Common.Box display="flex" justifyContent="center" py={2}>
            <SubmitButton />
          </Common.Box>

          <Common.Box fontSize={2} textAlign="center">
            <Common.Text
              style={{ cursor: 'pointer' }}
              textDecoration="underline"
            >
              <ReactRouter.Link to={`?${briteLoginNormalQuery}`}>
                {i18n.translate('brite-login-form.login-without-deposit')}
              </ReactRouter.Link>
            </Common.Text>
          </Common.Box>
        </Common.Box>
      </Forms.Provider>
    </Common.Box>
  )
}

export default BriteLoginForm
